/**
 * MapLibre GL JS の地図。背景タイルは Amazon Location Service の新世代 GeoMaps。
 *
 * - スタイル URL の組み立てとスタイル選択は mapStyle.ts（Standard / Monochrome / Hybrid）。
 * - center はラスベガス。MapLibre は標準でタッチ操作対応（PC/モバイル両対応）。
 * - ピン（クラスタリング込み）と軌跡を users から描画する。
 * - スタイル切替（setStyle）では背景側のソース/レイヤー/アイコンが破棄されるため、
 *   styledata で applyStyleDependents を呼び直して自前レイヤーを再構築する（要件19-2）。
 *
 * 要件 1-1 / 1-2 / 1-4 / 1-5 / 7-1〜7-6 / 19-1 / 19-2 に対応。
 */
import {
  GeolocateControl,
  Map as MaplibreMap,
  NavigationControl,
  setWorkerUrl,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre v6 は ESM 専用で、Web ワーカーを実行時に別ファイルから読み込む。
// バンドラはこの URL を自動解決できないため、Vite の ?worker&url で出力させて
// setWorkerUrl で明示する（これをしないとワーカーが読めずタイルが描画されない＝地図が真っ白）。
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// 最初の地図生成前に一度だけワーカー URL を設定する。
setWorkerUrl(maplibreWorkerUrl);
import type { Post, UserLocation } from '../api/client';
import { PinCluster } from './cluster';
import { PostLayer } from './postLayer';
import './pinStyle.css';
import { ensureTrailLayer, updateTrails } from './Trail';
import { ensureVenueLayer, setVenueLanguage } from './venueLayer';
import { resetMapLabelCache, setMapLabelLanguage } from './mapLabels';
import { buildStyleUrl, type MapStyleId } from './mapStyle';
import { MapToolbar } from './MapToolbar';
import { PortalControl } from './PortalControl';
import type { Lang } from '../i18n/lang';
import {
  applyInitialView,
  getFocusUserId,
  getInitialLatLng,
  LAS_VEGAS_CENTER,
  DEFAULT_ZOOM,
} from './initialView';

export interface MapViewProps {
  readonly users: UserLocation[];
  readonly serverTime: number;
  /** 表示言語（地図ラベル・会場名の切替用）。 */
  readonly lang: Lang;
  /** 背景スタイル（標準 / モノクロ / 衛星）。変更時に setStyle で差し替える。 */
  readonly mapStyle: MapStyleId;
  /** マップに表示するポスト（本文＋画像＋固定座標）。 */
  readonly posts: Post[];
  /** ポスト用のサーバー時刻（相対時刻表示用・任意）。 */
  readonly postsServerTime?: number;
  /** 選択中ユーザー（アコーディオンで選択）。軌跡を強調表示する。 */
  readonly selectedUserId?: string | null;
  /** 外部（アコーディオン等）から地図移動を呼ぶためのハンドル受け口。 */
  readonly flyToRef?: React.MutableRefObject<
    ((lng: number, lat: number) => void) | null
  >;
  /** 地図の初期ロード完了時に呼ばれる（ローディング表示の解除用）。 */
  readonly onReady?: () => void;
  /** 地図の空白部分がクリックされたとき（選択解除用）。 */
  readonly onDeselect?: () => void;
  /** 参加者ピンがクリックされたとき（軌跡強調の選択用）。 */
  readonly onSelectUser?: (userId: string) => void;
  /** ログイン中ユーザーの user_id（g_<sub>）。自分のポストに削除ボタンを出す判定用。 */
  readonly currentUserId?: string | null;
  /** 自分のポストの削除を実行するコールバック（吹き出しの削除ボタン）。 */
  readonly onDeletePost?: (postId: string) => void;
  /** 言語の変更（右上ツールバーの言語アイコン）。 */
  readonly onLangChange: (lang: Lang) => void;
  /** 背景スタイルの変更（右上ツールバーのスタイルアイコン）。 */
  readonly onMapStyleChange: (style: MapStyleId) => void;
  /** ライブアクティビティフィードの ON/OFF（右上ツールバーの 📡・タスク54）。 */
  readonly feedEnabled: boolean;
  readonly onFeedToggle: () => void;
}

export function MapView({
  users,
  serverTime,
  lang,
  mapStyle,
  posts,
  postsServerTime,
  selectedUserId = null,
  flyToRef,
  onReady,
  onDeselect,
  onSelectUser,
  currentUserId = null,
  onDeletePost,
  onLangChange,
  onMapStyleChange,
  feedEnabled,
  onFeedToggle,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const clusterRef = useRef<PinCluster | null>(null);
  const postLayerRef = useRef<PostLayer | null>(null);
  const loadedRef = useRef(false);
  // 右上ツールバーの描画先。MapLibre のコントロール枠として addControl に渡し、
  // 中身は createPortal で React が描く（PortalControl 参照）。初回レンダー時点で
  // 存在させたいので state ではなく ref の遅延生成にする（state だと1レンダー遅れて
  // 空のツールバーが見え、StrictMode の二重マウントで開閉状態もリセットされる）。
  const toolbarHostRef = useRef<HTMLDivElement | null>(null);
  if (!toolbarHostRef.current) {
    const el = document.createElement('div');
    el.className = 'maplibregl-ctrl map-toolbar-host';
    toolbarHostRef.current = el;
  }
  const toolbarHost = toolbarHostRef.current;
  // 初期表示は一度だけ適用する
  const initialViewDoneRef = useRef(false);
  // 最新の users/serverTime を常に保持する（load コールバックが初期値の空 users を
  // クロージャで捕まえて固定してしまう問題を避け、load 完了時に最新を反映するため）。
  const latestRef = useRef<{
    users: UserLocation[];
    serverTime: number;
    posts: Post[];
    postsServerTime: number;
    selectedUserId: string | null;
    currentUserId: string | null;
    onDeletePost?: (postId: string) => void;
    lang: Lang;
  }>({
    users,
    serverTime,
    posts,
    postsServerTime: postsServerTime ?? serverTime,
    selectedUserId,
    currentUserId,
    onDeletePost,
    lang,
  });
  latestRef.current = {
    users,
    serverTime,
    posts,
    postsServerTime: postsServerTime ?? serverTime,
    selectedUserId,
    currentUserId,
    onDeletePost,
    lang,
  };

  /**
   * 背景スタイルに依存する自前の要素を（再）構築する。
   * map load 直後と、スタイル切替（setStyle）後の styledata で呼ぶ。
   *
   * setStyle は背景側のソース/レイヤーだけでなく addImage で登録した画像（会場の絵文字アイコン）も
   * 破棄するため、ensureVenueLayer に再登録まで任せる。ラベルの原型キャッシュも新スタイル用に捨てる。
   * PinCluster / PostLayer は DOM Marker なのでスタイル差し替えの影響を受けない（作り直さない）。
   */
  function applyStyleDependents(map: MaplibreMap): void {
    // 新スタイルの text-field 原型を取り直すため、言語切替のキャッシュを破棄する
    resetMapLabelCache(map);
    ensureTrailLayer(map);
    ensureVenueLayer(map);
    const {
      lang: latestLang,
      users: latestUsers,
      selectedUserId: latestSelected,
    } = latestRef.current;
    setMapLabelLanguage(map, latestLang);
    setVenueLanguage(map, latestLang);
    updateTrails(map, latestUsers, latestSelected);
  }

  /**
   * 初期表示を「適切なタイミングで一度だけ」適用する。
   * - ?focus= 指定時: 対象ユーザーが users に現れるまで待つ（現れたら適用して確定）
   * - focus 指定なし: users が来た時点、または users が空でも1回は適用（現在地/既定）
   */
  function maybeApplyInitialView(map: MaplibreMap, list: UserLocation[]): void {
    if (initialViewDoneRef.current) {
      return;
    }
    // ?lat/lng は地図初期化時に適用済み。users を待たず即確定。
    if (getInitialLatLng()) {
      initialViewDoneRef.current = true;
      return;
    }
    const focusId = getFocusUserId();
    if (focusId) {
      // focus 対象がまだ現れていなければ保留（次の users 更新で再試行）
      const present = list.some((u) => u.user_id === focusId);
      if (!present) {
        return;
      }
    }
    initialViewDoneRef.current = true;
    void applyInitialView(map, list);
  }

  // 地図の初期化（一度だけ）
  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    // ?lat/lng があれば地図初期化時点でその座標を中心に（API を待たず即表示・要件18-6）
    const initialLatLng = getInitialLatLng();
    const map = new MaplibreMap({
      container: containerRef.current,
      style: buildStyleUrl(mapStyle),
      center: initialLatLng?.center ?? LAS_VEGAS_CENTER,
      zoom: initialLatLng?.zoom ?? DEFAULT_ZOOM,
    });
    // 右上は「ツールバー（スタイル/言語）→ 現在地」の順に縦積み。
    // top-* の addControl は末尾に追加するので、先に足したものが上に来る。
    map.addControl(new PortalControl(toolbarHost), 'top-right');
    // 「現在地へ」ボタン。クリックしたとき初めて位置情報の許可を求める
    // （初期表示では能動的に求めない＝プライバシー配慮）。許可後は現在地へ移動＋表示。
    map.addControl(
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserLocation: true,
      }),
      'top-right',
    );
    // 右下はズーム＋コンパス。attribution（AWS / HERE クレジット）は Map 生成時に
    // bottom-right へ自動追加されており、bottom-* の addControl は先頭挿入なので
    // 後から足すこちらが上・attribution が下になる。したがって attributionControl を
    // 自前にする必要はない（自前にすると既定の compact 指定と MapLibre クレジットを落とす）。
    map.addControl(new NavigationControl(), 'bottom-right');
    mapRef.current = map;

    // 外部（アコーディオンの名前クリック等）から地図移動を呼べるようにする
    if (flyToRef) {
      flyToRef.current = (lng: number, lat: number) => {
        const targetZoom = 15;
        // 移動アニメーション完了（zoomend）を待たず、目的ズームでの個別ピンを先に描画。
        // これでクリック直後に対象のアバターが表示され、「着いてから待つ」が解消される。
        clusterRef.current?.renderAtZoom(targetZoom);
        map.easeTo({ center: [lng, lat], zoom: targetZoom });
      };
    }

    map.on('load', () => {
      // 軌跡・会場レイヤーと地図ラベル/会場名の言語を適用（最新 lang/users は latestRef から）
      applyStyleDependents(map);
      clusterRef.current = new PinCluster(map, {
        // 自分のピンの強調（タスク51）。以前は selfUserId を誰も渡しておらず、強調が一度も出ていなかった。
        // ログイン中の ID は投稿の削除判定と同じ currentUserId を使う（以降の変化は下の effect で反映）。
        selfUserId: latestRef.current.currentUserId ?? undefined,
        onSelectUser: (userId) => onSelectUser?.(userId),
      });
      clusterRef.current.setLang(latestRef.current.lang);
      postLayerRef.current = new PostLayer(map);
      // 本人判定・削除の文脈・言語を設定（load 時点の最新値を使う）
      postLayerRef.current.setContext(
        latestRef.current.currentUserId,
        latestRef.current.onDeletePost,
      );
      postLayerRef.current.setLang(latestRef.current.lang);
      loadedRef.current = true;
      // load 完了時点の「最新」users/posts を反映（クロージャが捕まえた初期の空配列ではなく、
      // その後 API で届いた最新を latestRef 経由で使う。これで load が data より先でも
      // 後でも、load 完了時に必ず最新のピンが描かれ、次ポーリングまでの取りこぼしを防ぐ）。
      const {
        users: latestUsers,
        serverTime: latestServerTime,
        posts: latestPosts,
        postsServerTime: latestPostsServerTime,
      } = latestRef.current;
      clusterRef.current.setUsers(latestUsers, latestServerTime);
      postLayerRef.current.setPosts(latestPosts, latestPostsServerTime);
      // 軌跡は applyStyleDependents で最新 users を反映済み
      maybeApplyInitialView(map, latestUsers);

      // 地図の空白部分クリックで選択解除（会場ピン等のレイヤークリックは別ハンドラが処理）。
      // DOM Marker（参加者・ポストピン）のクリックではこのイベントは発火しない。
      map.on('click', () => {
        onDeselect?.();
      });

      // 地図が使える状態になったことを親へ通知（ローディング解除）
      onReady?.();
    });

    return () => {
      clusterRef.current?.destroy();
      clusterRef.current = null;
      postLayerRef.current?.destroy();
      postLayerRef.current = null;
      if (flyToRef) {
        flyToRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
    // 初期化は一度だけ（自分の ID・言語などは latestRef の最新値を使い、以降は各 effect で反映）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ログイン・ログアウトで自分のピンの強調を切り替える（タスク51）
  useEffect(() => {
    clusterRef.current?.setSelfUserId(currentUserId);
  }, [currentUserId]);

  // users / serverTime の更新をピン・軌跡へ反映
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current || !clusterRef.current) {
      return;
    }
    // 最新言語でピンを描く（ポーリング更新でも言語が維持される）。
    clusterRef.current.setLang(latestRef.current.lang);
    clusterRef.current.setUsers(users, serverTime);
    updateTrails(map, users, selectedUserId);
    // 初期表示の適用（?focus 対象の到着待ち・参加者フィット等）
    maybeApplyInitialView(map, users);
  }, [users, serverTime, selectedUserId]);

  // 選択ユーザーの変更だけで軌跡の強調を即反映する（データ更新を待たない）
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) {
      return;
    }
    updateTrails(map, users, selectedUserId);
    // selectedUserId の変更時のみ実行（users は上の effect が担当）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  // posts の更新をポストピンへ反映
  useEffect(() => {
    if (!loadedRef.current || !postLayerRef.current) {
      return;
    }
    // 本人判定・削除の文脈・言語を最新化してからポストを反映する。
    postLayerRef.current.setContext(currentUserId, onDeletePost);
    postLayerRef.current.setLang(lang);
    postLayerRef.current.setPosts(posts, postsServerTime ?? serverTime);
  }, [posts, postsServerTime, serverTime, currentUserId, onDeletePost, lang]);

  // 背景スタイルの切替（要件19-2）。setStyle は背景を丸ごと差し替えるため、styledata を待って
  // 自前レイヤー（軌跡・会場ピン・ラベル言語）を再構築する。初期スタイルは地図生成時に
  // 適用済みなので、適用済みスタイルと同じ間は何もしない。
  const appliedStyleRef = useRef<MapStyleId>(mapStyle);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || appliedStyleRef.current === mapStyle) {
      return;
    }
    appliedStyleRef.current = mapStyle;
    // 世代・レイヤー構成が大きく違うスタイル間を跨ぐため、差分適用（diff）には頼らない。
    map.setStyle(buildStyleUrl(mapStyle), { diff: false });
    map.once('styledata', () => {
      applyStyleDependents(map);
    });
    // applyStyleDependents は latestRef 経由で最新値を読むので依存に入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  // 言語切替を地図ラベル・会場名・ポスト吹き出しに反映。
  // 地図ラベルは setLayoutProperty のみ（スタイル再読込なし）。
  // ポストは言語を更新して再描画（開いていない吹き出しは次に開いた時に新言語で出る）。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) {
      return;
    }
    setMapLabelLanguage(map, lang);
    setVenueLanguage(map, lang);
    if (postLayerRef.current) {
      postLayerRef.current.setLang(lang);
      postLayerRef.current.setPosts(posts, postsServerTime ?? serverTime);
    }
    if (clusterRef.current) {
      // 参加者ピンの「N分前」も言語に合わせて再描画する。
      clusterRef.current.setLang(lang);
      clusterRef.current.setUsers(users, serverTime);
    }
    // posts/users はこの effect の直接の依存ではないが、lang 変更時に最新データで
    // 再描画する。データ更新時は各 effect が担当するため lang のみ依存にする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <>
      <div
        ref={containerRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      {/* 右上ツールバー。描画先は MapLibre が所有する箱（toolbarHost）。 */}
      {createPortal(
        <MapToolbar
          lang={lang}
          mapStyle={mapStyle}
          onLangChange={onLangChange}
          onMapStyleChange={onMapStyleChange}
          feedEnabled={feedEnabled}
          onFeedToggle={onFeedToggle}
        />,
        toolbarHost,
      )}
    </>
  );
}
